import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ArrowDownTrayIcon,
  StarIcon,
  TrophyIcon,
  DocumentIcon,
  DocumentChartBarIcon,
  PresentationChartBarIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  BookmarkIcon,
  WrenchScrewdriverIcon,
  LightBulbIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  addDoc,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useAuth } from '../../context/AuthContext';
import SharedTaskComponent from './SharedTaskComponent';
import WeeklyReportsUpload from '../../pages/features/WeeklyReports';
import {
  ProgramsService,
  ProgressService,
  SubmissionsService,
} from '../../services/programsService';

const accent = 'blue';
const accentCard = 'bg-white';
const accentCardBorder = 'border-black/20';

const TrainingDashboard = ({ onNavigateToTraining }) => {
  const { currentUser } = useAuth();
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [currentProgress, setCurrentProgress] = useState({ overall: 0, modules: [] });

  const [upcomingSessions, setUpcomingSessions] = useState([]);

  useEffect(() => {
    const loadProgramsAndProgress = async () => {
      if (!currentUser?.uid) {
        setLoadingProgress(false);
        return;
      }

      setLoadingProgress(true);
      try {
        const [allPrograms, userProgressArr] = await Promise.all([
          ProgramsService.getAllPrograms(),
          ProgressService.getUserProgress(currentUser.uid),
        ]);

        const programsList = allPrograms || [];
        const seenKeys = new Set();
        const uniquePrograms = programsList.filter(p => {
          const key = (p.type || p.title || p.id || '').toString().toLowerCase();
          if (!key) return false;
          if (seenKeys.has(key)) return false;
          seenKeys.add(key);

          if (
            p.type === 'capacity-building' ||
            p.title?.toLowerCase().includes('capacity building')
          ) {
            return false;
          }

          return true;
        });

        setPrograms(uniquePrograms);

        const progressMap = new Map((userProgressArr || []).map(p => [p.programId, p]));

        const moduleProgress = uniquePrograms.map(p => {
          const progress = progressMap.get(p.id);
          const pct = Math.max(0, Math.min(100, progress?.progressPercentage || 0));
          return {
            id: p.id,
            name: p.title || p.id,
            progress: pct,
          };
        });

        const nonZero = moduleProgress.filter(m => typeof m.progress === 'number');
        const overall = nonZero.length
          ? Math.round(nonZero.reduce((sum, m) => sum + (m.progress || 0), 0) / nonZero.length)
          : 0;

        setCurrentProgress({ overall, modules: moduleProgress });
      } catch (err) {
        setCurrentProgress({ overall: 0, modules: [] });
      } finally {
        setLoadingProgress(false);
      }
    };

    loadProgramsAndProgress();
  }, [currentUser?.uid]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl ${accentCard} p-4 shadow-lg md:p-6`}
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AcademicCapIcon className={`h-6 w-6 text-${accent}-600`} />
          <h2 className="text-xl font-semibold text-black">Training Dashboard</h2>
        </div>
        {onNavigateToTraining && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onNavigateToTraining}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <BookOpenIcon className="h-4 w-4" />
            View All Training Programs
          </motion.button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-black">Overall Progress</h3>
          <div
            className={`rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white md:p-6`}
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm">Program Completion</span>
              <span className="text-2xl font-bold">{currentProgress.overall}%</span>
            </div>
            <div className="mb-4 h-3 w-full rounded-full bg-white/20">
              <div
                className="h-3 rounded-full bg-white transition-all duration-500"
                style={{ width: `${currentProgress.overall}%` }}
              />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <TrophyIcon className="h-4 w-4" />
              <span>You are on track to complete in 3 weeks!</span>
            </div>
          </div>

          {/* Quick Access to Training Programs */}
          {onNavigateToTraining && (
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h4 className="mb-3 text-sm font-medium text-blue-900">
                Quick Access to Training Programs
              </h4>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onNavigateToTraining}
                  className="flex items-center gap-3 rounded-lg bg-white p-3 text-left transition-colors hover:bg-blue-100"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <BookOpenIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Design-A-Business</p>
                    <p className="text-xs text-blue-700">Business planning fundamentals</p>
                  </div>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onNavigateToTraining}
                  className="flex items-center gap-3 rounded-lg bg-white p-3 text-left transition-colors hover:bg-blue-100"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                    <ChartBarIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-900">
                      Costing & Management Accounting
                    </p>
                    <p className="text-xs text-green-700">Financial management skills</p>
                  </div>
                </motion.button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {loadingProgress && (
              <div className="flex items-center gap-2 text-sm text-black/70">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                {/* <span>Loading your program progress...</span> */}
              </div>
            )}
            {!loadingProgress && currentProgress.modules.length === 0 && (
              <div className="rounded-lg border border-black/10 bg-gray-50 p-4 text-sm text-black/70">
                No programs found. Once programs are assigned, your progress will appear here.
              </div>
            )}
            {!loadingProgress &&
              currentProgress.modules.map((module, index) => (
                <motion.div
                  key={module.id || module.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`rounded-lg border ${accentCardBorder} p-4`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-black">{module.name}</h4>
                    <span className="text-sm text-black/70">Overall progress</span>
                  </div>
                  <div className="mb-2 h-2 w-full rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full bg-blue-600 transition-all duration-500"
                      style={{ width: `${module.progress}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <ClockIcon className="h-3 w-3" />
                    <span>{module.progress}% complete</span>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
        <div>
          <h3 className="mb-4 text-lg font-medium text-black">Upcoming Sessions</h3>
          <div className="space-y-3">
            {upcomingSessions.length === 0 ? (
              <div className="rounded-lg border border-dashed border-black/20 bg-white p-6 text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                  <CalendarIcon className="h-5 w-5 text-gray-500" />
                </div>
                <p className="mb-2 text-sm font-medium text-black">No sessions scheduled yet</p>
                <p className="mb-4 text-xs text-black/60">
                  When your coach schedules sessions, they will appear here.
                </p>
                {onNavigateToTraining && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onNavigateToTraining}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Explore Training Programs
                  </motion.button>
                )}
              </div>
            ) : (
              upcomingSessions.map((session, index) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`rounded-lg border ${accentCardBorder} p-4 transition-colors hover:border-blue-300`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-black">{session.title}</h4>
                    <span
                      className={`rounded-full px-2 py-1 text-xs ${
                        session.status === 'confirmed'
                          ? 'bg-emerald-50 text-emerald-600'
                          : 'bg-amber-50 text-amber-600'
                      }`}
                    >
                      {session.status}
                    </span>
                  </div>
                  <div className="mb-3 space-y-1 text-sm text-black/70">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      <span>{new Date(session.date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ClockIcon className="h-4 w-4" />
                      <span>
                        {session.time} ({session.duration})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <UserIcon className="h-4 w-4" />
                      <span>Coach: {session.coach}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      Join Session
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="rounded-lg border border-black/20 px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                    >
                      Reschedule
                    </motion.button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const MilestoneTracking = ({ onNavigateToTraining }) => {
  const { currentUser } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [progressByProgram, setProgressByProgram] = useState({});
  const [loadingMilestones, setLoadingMilestones] = useState(true);

  useEffect(() => {
    if (!currentUser?.uid) return;

    let unsubscribeFns = [];
    const load = async () => {
      setLoadingMilestones(true);
      try {
        const allPrograms = await ProgramsService.getAllPrograms();
        const seen = new Set();
        const unique = (allPrograms || []).filter(p => {
          const key = (p.type || p.title || p.id || '').toString().toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);

          if (
            p.type === 'capacity-building' ||
            p.title?.toLowerCase().includes('capacity building')
          ) {
            return false;
          }

          return true;
        });
        setPrograms(unique);

        unique.forEach(p => {
          const unsub = ProgressService.listenToUserProgress(currentUser.uid, p.id, progress => {
            setProgressByProgram(prev => ({ ...prev, [p.id]: progress }));
          });
          unsubscribeFns.push(unsub);
        });
      } catch (e) {
      } finally {
        setLoadingMilestones(false);
      }
    };
    load();

    return () => {
      unsubscribeFns.forEach(fn => {
        try {
          if (typeof fn === 'function') fn();
        } catch (_) {}
      });
    };
  }, [currentUser?.uid]);

  const getStatusColor = status => 'bg-blue-50 text-blue-600 border-blue-200';

  const getCategoryIcon = category => {
    switch (category) {
      case 'planning':
        return ClipboardDocumentListIcon;
      case 'research':
        return MagnifyingGlassIcon;
      case 'presentation':
        return PresentationChartBarIcon;
      default:
        return DocumentTextIcon;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl ${accentCard} p-4 shadow-lg md:p-6`}
    >
      <div className="mb-6 flex items-center gap-3">
        <ChartBarIcon className={`h-6 w-6 text-${accent}-600`} />
        <h2 className="text-xl font-semibold text-black">Milestone Tracking</h2>
      </div>

      <div className="space-y-4">
        {loadingMilestones && (
          <div className="flex items-center gap-2 text-sm text-black/70">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
            <span>Loading milestones...</span>
          </div>
        )}

        {!loadingMilestones && programs.length === 0 && (
          <div className="rounded-lg border border-dashed border-black/20 bg-white p-6 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <ChartBarIcon className="h-5 w-5 text-gray-500" />
            </div>
            <p className="mb-2 text-sm font-medium text-black">No milestones to show yet</p>
            <p className="mb-4 text-xs text-black/60">Your program milestones will appear here.</p>
            {onNavigateToTraining && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onNavigateToTraining}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Explore Training Programs
              </motion.button>
            )}
          </div>
        )}

        {programs.map((program, index) => {
          const progress = progressByProgram[program.id];
          const pct = Math.max(0, Math.min(100, progress?.progressPercentage || 0));
          const status = pct === 100 ? 'completed' : progress?.status || 'pending';
          const category =
            program.type === 'design-a-business'
              ? 'planning'
              : program.type === 'costing-accounting'
                ? 'research'
                : 'presentation';

          return (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-lg border ${accentCardBorder} p-4 transition-colors hover:border-blue-300`}
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="text-blue-600">
                    {(() => {
                      const IconComponent = getCategoryIcon(category);
                      return <IconComponent className="h-6 w-6" />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-black">{program.title || program.id}</h3>
                    {program.description && (
                      <p className="mt-1 text-sm text-black/70">{program.description}</p>
                    )}
                  </div>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${getStatusColor(status)}`}
                >
                  {status}
                </span>
              </div>

              <div className="mb-3">
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-black/70">Progress</span>
                  <span className="font-medium text-black">{pct}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100">
                  <div
                    className="h-2 rounded-full bg-blue-600 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  {program.dueDate ? (
                    <>
                      <CalendarIcon className="h-4 w-4" />
                      <span>Due: {new Date(program.dueDate).toLocaleDateString()}</span>
                    </>
                  ) : (
                    <span className="text-black/60">No due date</span>
                  )}
                </div>
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onNavigateToTraining}
                    className="rounded-lg border border-black/20 px-3 py-1 text-xs font-medium text-black transition-colors hover:bg-black/5"
                  >
                    View Details
                  </motion.button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

const CoachView = ({ assigned, assignedEmail, assignedCoachName, assignedCoachEmail }) => {
  const { currentUser } = useAuth();
  const [coach, setCoach] = useState(null);
  const [moderatorDetails, setModeratorDetails] = useState(null);
  const [coachDetails, setCoachDetails] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loadingModerator, setLoadingModerator] = useState(false);
  const [loadingCoach, setLoadingCoach] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  useEffect(() => {
    setCoach({
      name: assigned,
      email: assignedEmail,
    });

    const fetchModerator = async () => {
      if (!assignedEmail) {
        toast.error('No assignedEmail provided for moderator');
        return;
      }

      setLoadingModerator(true);

      try {
        const q = query(collection(firestore, 'moderator'), where('email', '==', assignedEmail));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const moderatorData = querySnapshot.docs[0].data();
          setModeratorDetails(moderatorData);
        } else {
          toast.error('No Business Advisor found with email: ' + assignedEmail);
          setModeratorDetails(null);
        }
      } catch (error) {
        toast.error('Error fetching Business Advisor details: ' + error.message);
        setModeratorDetails(null);
      } finally {
        setLoadingModerator(false);
      }
    };

    const fetchCoach = async () => {
      if (!assignedCoachEmail) {
        toast.error('No assignedCoachEmail provided for coach');
        return;
      }

      setLoadingCoach(true);

      try {
        const q = query(collection(firestore, 'coaches'), where('email', '==', assignedCoachEmail));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const coachData = querySnapshot.docs[0].data();
          setCoachDetails(coachData);
        } else {
          toast.error('No Business Coach found with email: ' + assignedCoachEmail);
          setCoachDetails(null);
        }
      } catch (error) {
        toast.error('Error fetching Business Coach details: ' + error.message);
        setCoachDetails(null);
      } finally {
        setLoadingCoach(false);
      }
    };

    fetchModerator();
    fetchCoach();
  }, [assigned, assignedEmail, assignedCoachName, assignedCoachEmail]);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const messagesRef = collection(firestore, 'messages');
    const qRef = query(messagesRef, where('clientId', '==', currentUser.uid));
    const unsub = onSnapshot(
      qRef,
      snapshot => {
        const items = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(m => (m.category || 'team') === 'team');
        items.sort((a, b) => {
          const ta = a.createdAt?.toDate
            ? a.createdAt.toDate().getTime()
            : new Date(a.createdAt || 0).getTime();
          const tb = b.createdAt?.toDate
            ? b.createdAt.toDate().getTime()
            : new Date(b.createdAt || 0).getTime();
          return ta - tb;
        });
        setMessages(items);
      },
      err => {
        toast.error('Messages listener error: ' + err.message);
      }
    );

    return () => unsub();
  }, [currentUser?.uid]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !currentUser?.uid) return;
    try {
      const payload = {
        category: 'team',
        clientId: currentUser.uid,
        text: newMessage.trim(),
        authorEmail: currentUser.email || '',
        authorName: currentUser.displayName || currentUser.email || 'Client',
        authorRole: 'client',
        recipients: [assignedEmail, assignedCoachEmail].filter(Boolean),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await addDoc(collection(firestore, 'messages'), payload);
      setNewMessage('');
      toast.success('Message sent!');
    } catch (e) {
      toast.error('Failed to send message');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl ${accentCard} p-6 shadow-lg`}
    >
      <div className="mb-6 flex items-center gap-3">
        <UserIcon className={`h-6 w-6 text-${accent}-600`} />
        <h2 className="text-xl font-semibold text-black">Your Team</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <div className={`rounded-lg border ${accentCardBorder} p-4`}>
            <h4 className="mb-3 text-lg font-medium text-black">Business Coach and/or Mentor</h4>
            <div className="mb-4">
              <h3 className="font-medium text-black">
                {loadingCoach ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                    <span>Loading coach details...</span>
                  </div>
                ) : coachDetails ? (
                  `${coachDetails.prefix || ''} ${coachDetails.firstname || ''} ${coachDetails.lastname || ''}`.trim()
                ) : assignedCoachName ? (
                  assignedCoachName
                ) : (
                  'No coach assigned'
                )}
              </h3>
              <div className="flex items-center gap-1 text-sm text-black/70">
                <span>{assignedCoachEmail}</span>
                {loadingCoach && <span className="text-blue-600">(Loading details...)</span>}
              </div>

              {coachDetails && (
                <div className="mt-2 space-y-1 text-sm text-black/70">
                  {coachDetails.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Phone:</span>
                      <span>{coachDetails.phone}</span>
                    </div>
                  )}
                  {coachDetails.organization && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Organization:</span>
                      <span>{coachDetails.organization}</span>
                    </div>
                  )}
                  {coachDetails.location && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Location:</span>
                      <span>{coachDetails.location}</span>
                    </div>
                  )}
                  {coachDetails.status && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Status:</span>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          coachDetails.status === 'active'
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-red-50 text-red-600'
                        }`}
                      >
                        {coachDetails.status}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!loadingCoach && !coachDetails && assignedCoachEmail && (
                <div className="mt-2 rounded bg-yellow-50 p-2 text-xs text-yellow-700">
                  <strong>Debug Info:</strong> No coach found with email: {assignedCoachEmail}
                </div>
              )}
            </div>

            <div className="mb-4">
              <h5 className="mb-2 text-sm font-medium text-black">Coach Expertise</h5>
              <div className="flex flex-wrap gap-2">
                {coachDetails?.specialization && (
                  <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-600">
                    {coachDetails.specialization}
                  </span>
                )}
                <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-600">
                  Business Strategy
                </span>
                <span className="rounded-full bg-blue-50 px-2 py-1 text-xs text-blue-600">
                  Financial Planning
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Schedule Session
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedProfile('coach');
                  setShowProfileModal(true);
                }}
                className="flex-1 rounded-lg border border-black/20 px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
              >
                View Profile
              </motion.button>
            </div>
          </div>
        </div>

        <div>
          <div className={`rounded-lg border ${accentCardBorder} p-4`}>
            <h4 className="mb-3 text-lg font-medium text-black">Business Advisor</h4>
            <div className="mb-4">
              <h3 className="font-medium text-black">
                {loadingModerator ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                    <span>Loading moderator details...</span>
                  </div>
                ) : moderatorDetails ? (
                  `${moderatorDetails.prefix || ''} ${moderatorDetails.firstname || ''} ${moderatorDetails.lastname || ''}`.trim()
                ) : assigned ? (
                  assigned
                ) : (
                  'No moderator assigned'
                )}
              </h3>
              <div className="flex items-center gap-1 text-sm text-black/70">
                <span>{assignedEmail}</span>
                {loadingModerator && <span className="text-blue-600">(Loading details...)</span>}
              </div>

              {moderatorDetails && (
                <div className="mt-2 space-y-1 text-sm text-black/70">
                  {moderatorDetails.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Phone:</span>
                      <span>{moderatorDetails.phone}</span>
                    </div>
                  )}
                  {moderatorDetails.company && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Company:</span>
                      <span>{moderatorDetails.company}</span>
                    </div>
                  )}
                  {moderatorDetails.department && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Department:</span>
                      <span>{moderatorDetails.department}</span>
                    </div>
                  )}
                  {moderatorDetails.status && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">Status:</span>
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${
                          moderatorDetails.status === 'active'
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-red-50 text-red-600'
                        }`}
                      >
                        {moderatorDetails.status}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!loadingModerator && !moderatorDetails && assignedEmail && (
                <div className="mt-2 rounded bg-yellow-50 p-2 text-xs text-yellow-700">
                  <strong>Debug Info:</strong> No moderator found with email: {assignedEmail}
                </div>
              )}
            </div>

            <div className="mb-4">
              <h5 className="mb-2 text-sm font-medium text-black">Business Advisor Expertise</h5>
              <div className="flex flex-wrap gap-2">
                {moderatorDetails?.department && (
                  <span className="rounded-full bg-green-50 px-2 py-1 text-xs text-green-600">
                    {moderatorDetails.department}
                  </span>
                )}
                <span className="rounded-full bg-green-50 px-2 py-1 text-xs text-green-600">
                  Program Management
                </span>
                <span className="rounded-full bg-green-50 px-2 py-1 text-xs text-green-600">
                  Client Support
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                Schedule Session
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedProfile('moderator');
                  setShowProfileModal(true);
                }}
                className="flex-1 rounded-lg border border-black/20 px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
              >
                View Profile
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className={`rounded-lg border ${accentCardBorder}`}>
          <div className="border-b border-black/10 bg-gray-50 px-4 py-3">
            <h3 className="font-medium text-black">Messages with your Team</h3>
          </div>

          <div className="h-64 overflow-y-auto p-4">
            <div className="space-y-3">
              {messages.map(message => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${
                    (message.authorEmail || '').toLowerCase() ===
                    (currentUser?.email || '').toLowerCase()
                      ? 'justify-end'
                      : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-xs rounded-lg px-3 py-2 ${
                      (message.authorEmail || '').toLowerCase() ===
                      (currentUser?.email || '').toLowerCase()
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-black'
                    }`}
                  >
                    <p className="text-sm">{message.text || message.message}</p>
                    <p className="mt-1 text-xs opacity-70">
                      {message.createdAt?.toDate
                        ? message.createdAt.toDate().toLocaleTimeString()
                        : new Date(
                            message.createdAt || message.timestamp || Date.now()
                          ).toLocaleTimeString()}
                    </p>
                  </div>
                </motion.div>
              ))}
              {messages.length === 0 && (
                <div className="text-center text-sm text-black/60">No messages yet</div>
              )}
            </div>
          </div>

          <div className="border-t border-black/10 p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                className="flex-1 rounded-lg border border-black/20 px-3 py-2 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600/20"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={sendMessage}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
              >
                Send
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showProfileModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowProfileModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-black">
                  {selectedProfile === 'coach' ? 'Coach Profile' : 'Business Advisor Profile'}
                </h2>
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    setSelectedProfile(null);
                  }}
                  className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {selectedProfile === 'coach' && coachDetails ? (
                <div className="space-y-6">
                  {/* Coach Section */}
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <h4 className="mb-3 text-lg font-semibold text-blue-700">Coach Information</h4>
                    <div className="flex items-start gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-lg font-bold text-blue-600">
                        {`${coachDetails.firstname?.[0] || ''}${coachDetails.lastname?.[0] || ''}`.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-black">
                          {coachDetails.prefix} {coachDetails.firstname} {coachDetails.lastname}
                        </h3>
                        <p className="text-gray-600">{assignedCoachEmail}</p>
                        <p className="text-sm text-gray-500">
                          {coachDetails.organization} • {coachDetails.location}
                        </p>
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                              coachDetails.status === 'active'
                                ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-red-50 text-red-600'
                            }`}
                          >
                            {coachDetails.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <h4 className="mb-3 text-lg font-medium text-black">
                      Coach Expertise & Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {coachDetails.specialization && (
                        <span className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-600">
                          {coachDetails.specialization}
                        </span>
                      )}
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-600">
                        Business Strategy
                      </span>
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-600">
                        Financial Planning
                      </span>
                      <span className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-600">
                        Mentoring
                      </span>
                    </div>
                  </div>
                </div>
              ) : selectedProfile === 'moderator' && moderatorDetails ? (
                <div className="space-y-6">
                  {/* Moderator Section */}
                  <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                    <h4 className="mb-3 text-lg font-semibold text-green-700">
                      Business Advisor Information
                    </h4>
                    <div className="flex items-start gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-lg font-bold text-green-600">
                        {`${moderatorDetails.firstname?.[0] || ''}${moderatorDetails.lastname?.[0] || ''}`.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-black">
                          {moderatorDetails.prefix} {moderatorDetails.firstname}{' '}
                          {moderatorDetails.lastname}
                        </h3>
                        <p className="text-gray-600">{assignedEmail}</p>
                        <p className="text-sm text-gray-500">
                          {moderatorDetails.department} • {moderatorDetails.company}
                        </p>
                        <div className="mt-2">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                              moderatorDetails.status === 'active'
                                ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-red-50 text-red-600'
                            }`}
                          >
                            {moderatorDetails.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <h4 className="mb-3 text-lg font-medium text-black">
                      Business Advisor Expertise & Skills
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {moderatorDetails.department && (
                        <span className="rounded-full bg-green-50 px-3 py-1 text-sm text-green-600">
                          {moderatorDetails.department}
                        </span>
                      )}
                      <span className="rounded-full bg-green-50 px-3 py-1 text-sm text-green-600">
                        Program Management
                      </span>
                      <span className="rounded-full bg-green-50 px-3 py-1 text-sm text-green-600">
                        Client Support
                      </span>
                      <span className="rounded-full bg-green-50 px-3 py-1 text-sm text-green-600">
                        Business Development
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-3 font-medium text-white transition-colors hover:bg-blue-700"
                    >
                      Schedule Session
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="rounded-lg border border-gray-300 px-4 py-3 font-medium text-black transition-colors hover:bg-gray-50"
                      onClick={() => {
                        setShowProfileModal(false);
                        setSelectedProfile(null);
                      }}
                    >
                      Close
                    </motion.button>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                    <UserIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-black">
                    Team Information Not Available
                  </h3>
                  <p className="mb-4 text-gray-500">
                    {assigned || assignedCoachName
                      ? `Assigned: ${assignedCoachName || assigned}`
                      : 'No team members have been assigned yet.'}
                  </p>
                  <button
                    onClick={() => {
                      setShowProfileModal(false);
                      setSelectedProfile(null);
                    }}
                    className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ResourceHub = () => {
  const [resources, setResources] = useState([
    {
      id: 1,
      name: 'Business Plan Template',
      type: 'pdf',
      size: '2.4 MB',
      category: 'templates',
      description: 'Comprehensive business plan template with financial projections',
      downloads: 45,
      rating: 4.8,
      url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf', // Sample PDF
    },
    {
      id: 2,
      name: 'Market Research Guide',
      type: 'docx',
      size: '1.8 MB',
      category: 'guides',
      description: 'Step-by-step guide for conducting market research',
      downloads: 32,
      rating: 4.6,
      url: 'https://file-examples.com/storage/fe68c8b7b7b7b7b7b7b7b7b/2017/10/file_example_DOCX_10kB.docx', // Sample DOCX
    },
    {
      id: 3,
      name: 'Financial Projections Calculator',
      type: 'xlsx',
      size: '3.2 MB',
      category: 'tools',
      description: 'Excel calculator for financial projections and cash flow',
      downloads: 28,
      rating: 4.9,
      url: 'https://file-examples.com/storage/fe68c8b7b7b7b7b7b7b7b7b/2017/10/file_example_XLSX_10kB.xlsx', // Sample XLSX
    },
    {
      id: 4,
      name: 'Pitch Deck Examples',
      type: 'pptx',
      size: '5.1 MB',
      category: 'examples',
      description: 'Collection of successful pitch deck examples',
      downloads: 38,
      rating: 4.7,
      url: 'https://file-examples.com/storage/fe68c8b7b7b7b7b7b7b7b7b/2017/10/file_example_PPTX_10kB.pptx', // Sample PPTX
    },
  ]);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'All Resources', icon: BookOpenIcon },
    { id: 'templates', name: 'Templates', icon: ClipboardDocumentListIcon },
    { id: 'guides', name: 'Guides', icon: BookmarkIcon },
    { id: 'tools', name: 'Tools', icon: WrenchScrewdriverIcon },
    { id: 'examples', name: 'Examples', icon: LightBulbIcon },
  ];

  const getFileIcon = type => {
    switch (type) {
      case 'pdf':
        return DocumentIcon;
      case 'docx':
        return DocumentTextIcon;
      case 'xlsx':
        return DocumentChartBarIcon;
      case 'pptx':
        return PresentationChartBarIcon;
      default:
        return FolderIcon;
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch =
      resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const downloadResource = async resource => {
    try {
      // Show loading state
      const toastId = toast.loading(`Downloading ${resource.name}...`);

      // If resource has a URL, download it directly
      if (resource.url) {
        const link = document.createElement('a');
        link.href = resource.url;
        link.download = resource.name;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.update(toastId, {
          render: `${resource.name} download started!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        await new Promise(resolve => setTimeout(resolve, 1000));

        toast.update(toastId, {
          render: `${resource.name} downloaded successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error(`Failed to download ${resource.name}`);
    }
  };

  const previewResource = resource => {
    try {
      if (resource.url) {
        window.open(resource.url, '_blank');
      } else {
        toast.info(`Preview for ${resource.name} - This would open a preview window`);
      }
    } catch (error) {
      toast.error(`Failed to preview ${resource.name}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl ${accentCard} p-6 shadow-lg`}
    >
      <div className="mb-6 flex items-center gap-3">
        <BookOpenIcon className={`h-6 w-6 text-${accent}-600`} />
        <h2 className="text-xl font-semibold text-black">Resource Hub</h2>
      </div>

      <div className="mb-6 flex flex-col gap-4 sm:flex-row">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-black/20 py-2 pl-10 pr-3 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600/20"
            />
          </div>
        </div>
        <div className="flex gap-2">
          {categories.map(category => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-black hover:bg-gray-200'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                <span>{category.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredResources.map((resource, index) => {
          const FileIcon = getFileIcon(resource.type);
          return (
            <motion.div
              key={resource.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`rounded-lg border ${accentCardBorder} p-4 transition-colors hover:border-blue-300`}
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="text-blue-600">
                  <FileIcon className="h-8 w-8" />
                </div>
                <div className="flex items-center gap-1 text-sm text-blue-600">
                  <StarIcon className="h-4 w-4 text-yellow-500" />
                  <span>{resource.rating}</span>
                </div>
              </div>

              <h3 className="mb-2 font-medium text-black">{resource.name}</h3>
              <p className="mb-3 text-sm text-black/70">{resource.description}</p>

              <div className="mb-3 space-y-1 text-xs text-blue-600">
                <div className="flex items-center gap-2">
                  <span>Size: {resource.size}</span>
                  <span>•</span>
                  <span>{resource.downloads} downloads</span>
                </div>
              </div>

              <div className="flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => downloadResource(resource)}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Download
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => previewResource(resource)}
                  className="rounded-lg border border-black/20 px-3 py-2 text-sm font-medium text-black transition-colors hover:bg-black/5"
                >
                  Preview
                </motion.button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredResources.length === 0 && (
        <div className="py-8 text-center">
          <BookOpenIcon className="mx-auto mb-4 h-12 w-12 text-blue-400" />
          <p className="text-blue-600">No resources found matching your criteria.</p>
        </div>
      )}
    </motion.div>
  );
};

const TasksView = () => {
  const { currentUser } = useAuth();

  return (
    <SharedTaskComponent
      userRole="client"
      userEmail={currentUser?.email}
      canCreate={false}
      canEdit={true}
      canDelete={false}
      canAssign={false}
      title="My Assigned Tasks"
    />
  );
};

const SessionNotesView = () => {
  const { currentUser } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const notesQuery = query(
      collection(firestore, 'sessionNotes'),
      where('clientId', '==', currentUser.uid),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(
      notesQuery,
      snapshot => {
        const sessionsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSessions(sessionsData);
        setLoading(false);
      },
      error => {
        console.error('Failed to fetch sessions:', error);
        const simpleQuery = query(
          collection(firestore, 'sessionNotes'),
          where('clientId', '==', currentUser.uid)
        );
        onSnapshot(
          simpleQuery,
          snapshot => {
            const sessionsData = snapshot.docs
              .map(doc => ({
                id: doc.id,
                ...doc.data(),
              }))
              .sort((a, b) => {
                const dateA = a.date ? new Date(a.date).getTime() : 0;
                const dateB = b.date ? new Date(b.date).getTime() : 0;
                return dateB - dateA;
              });
            setSessions(sessionsData);
            setLoading(false);
          },
          () => {
            toast.error('Failed to fetch session notes');
            setLoading(false);
          }
        );
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl bg-white p-6 shadow-lg"
      >
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600"></div>
            <p className="text-black/60">Loading session notes...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl bg-white p-4 shadow-lg sm:p-6"
    >
      <div className="mb-6 flex items-center gap-3">
        <DocumentTextIcon className="h-6 w-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-black">Session Notes</h2>
      </div>

      <div className="space-y-4">
        {sessions.length === 0 ? (
          <div className="py-12 text-center">
            <DocumentTextIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p className="text-gray-600">No session notes available yet.</p>
            <p className="mt-2 text-sm text-gray-500">
              Your coach will add notes after your sessions.
            </p>
          </div>
        ) : (
          sessions.map((session, index) => {
            const sessionDate = session.date
              ? new Date(session.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : 'N/A';

            return (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="cursor-pointer rounded-lg border border-black/20 p-4 transition-colors hover:border-black/30"
                onClick={() => setSelectedSession(session)}
              >
                <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <h4 className="font-medium text-black">
                        Session with {session.coachName || 'Coach'}
                      </h4>
                      <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-600">
                        {session.type || 'strategy'}
                      </span>
                      {session.rating > 0 && (
                        <div className="flex items-center gap-1">
                          <StarIcon className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-medium">{session.rating}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-black/70">
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{sessionDate}</span>
                      </div>
                      {session.duration && (
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>{session.duration}</span>
                        </div>
                      )}
                    </div>
                    {session.notes && (
                      <p className="mt-2 line-clamp-2 text-sm text-black/70">{session.notes}</p>
                    )}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedSession(session);
                    }}
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View Details
                  </motion.button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {selectedSession && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedSession(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-black">
                  Session with {selectedSession.coachName || 'Coach'}
                </h3>
                <p className="text-sm text-black/60">
                  {selectedSession.date
                    ? new Date(selectedSession.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'N/A'}{' '}
                  • {selectedSession.duration || 'N/A'} •{' '}
                  {selectedSession.type?.replace(/\b\w/g, l => l.toUpperCase()) || 'Strategy'}
                </p>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="rounded-lg p-2 hover:bg-gray-100"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-gray-100 p-4">
                <p className="text-xs uppercase tracking-wide text-black/50">Rating</p>
                <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-black">
                  <StarIcon className="h-5 w-5 text-yellow-500" />
                  {selectedSession.rating > 0 ? selectedSession.rating : 'Not rated'}
                </div>
              </div>
              <div className="rounded-xl border border-gray-100 p-4">
                <p className="text-xs uppercase tracking-wide text-black/50">Status</p>
                <span className="mt-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                  {selectedSession.status || 'completed'}
                </span>
              </div>
            </div>

            <div className="mb-6 rounded-2xl bg-gray-50 p-4">
              <p className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wide text-black/50">
                <DocumentTextIcon className="h-4 w-4" />
                Session Notes
              </p>
              <p className="text-sm leading-relaxed text-black/80">
                {selectedSession.notes || 'No notes provided.'}
              </p>
            </div>

            {selectedSession.actionItems && selectedSession.actionItems.length > 0 && (
              <div>
                <p className="mb-3 text-xs uppercase tracking-wide text-black/50">Action Items</p>
                <ul className="space-y-2 text-sm text-black/80">
                  {selectedSession.actionItems.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-black/30" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

const ClientsCRM = ({ onNavigateToTraining }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    sessionsCompleted: 0,
    milestonesAchieved: 0,
    resourcesDownloaded: 0,
    coachRating: 0,
  });
  const [userProgressByProgram, setUserProgressByProgram] = useState({});
  const [submissionsCount, setSubmissionsCount] = useState(0);

  // Fetch current user's client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (!currentUser?.email) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const q = query(collection(firestore, 'clients'), where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const clientDoc = querySnapshot.docs[0];
          const clientData = { id: clientDoc.id, ...clientDoc.data() };
          setClientData(clientData);
        } else {
          setClientData(null);
        }
      } catch (error) {
        setClientData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [currentUser?.email]);

  // Real-time: listen to progress per program and submissions for this user
  useEffect(() => {
    if (!currentUser?.uid) return;

    let unsubscribeFunctions = [];

    const setupListeners = async () => {
      try {
        const programs = await ProgramsService.getAllPrograms();
        // Filter out capacity building program for clients
        const filteredPrograms = programs.filter(program => {
          return !(
            program.type === 'capacity-building' ||
            program.title?.toLowerCase().includes('capacity building')
          );
        });

        filteredPrograms.forEach(program => {
          const unsub = ProgressService.listenToUserProgress(
            currentUser.uid,
            program.id,
            progress => {
              setUserProgressByProgram(prev => ({ ...prev, [program.id]: progress }));
            }
          );
          unsubscribeFunctions.push(unsub);
        });

        const unsubSubs = SubmissionsService.subscribeToSubmissions(
          currentUser.uid,
          null,
          submissions => {
            setSubmissionsCount(submissions?.length || 0);
          }
        );
        unsubscribeFunctions.push(unsubSubs);
      } catch (e) {}
    };

    setupListeners();

    return () => {
      unsubscribeFunctions.forEach(unsub => {
        try {
          if (typeof unsub === 'function') unsub();
        } catch (_) {}
      });
    };
  }, [currentUser?.uid]);
  useEffect(() => {
    let sessionsCompleted = 0;
    let milestonesAchieved = 0;

    Object.values(userProgressByProgram).forEach(progress => {
      if (!progress) return;
      const lessons = progress.lessons || {};
      sessionsCompleted += Object.values(lessons).filter(l => l && l.completed).length;
      if ((progress.progressPercentage || 0) >= 100) milestonesAchieved += 1;
    });

    setStats(prev => ({
      ...prev,
      sessionsCompleted,
      milestonesAchieved,
      resourcesDownloaded: submissionsCount,
    }));
  }, [userProgressByProgram, submissionsCount]);

  // Coach rating realtime (if present)
  useEffect(() => {
    if (!clientData?.assignedCoachEmail) return;
    const qRef = query(
      collection(firestore, 'coaches'),
      where('email', '==', clientData.assignedCoachEmail)
    );
    const unsub = onSnapshot(qRef, snap => {
      if (!snap.empty) {
        const coach = snap.docs[0].data();
        setStats(prev => ({ ...prev, coachRating: coach?.rating || 0 }));
      } else {
        setStats(prev => ({ ...prev, coachRating: 0 }));
      }
    });
    return () => unsub();
  }, [clientData?.assignedCoachEmail]);

  const tabs = [
    { id: 'dashboard', name: 'Training Dashboard', icon: AcademicCapIcon },
    // { id: 'milestones', name: 'Milestones', icon: ChartBarIcon },
    { id: 'coach', name: 'My Coach', icon: UserIcon },
    { id: 'tasks', name: 'Tasks', icon: ClockIcon },
    { id: 'session-notes', name: 'Session Notes', icon: DocumentTextIcon },
    { id: 'resources', name: 'Resources', icon: BookOpenIcon },
    // { id: 'weeklyreports', name: 'Weekly Reports', icon: DocumentTextIcon },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600"></div>
              <p className="text-lg text-black">Loading your dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <h1 className="mb-4 text-2xl font-bold text-black">Client Data Not Found</h1>
              <p className="text-black/70">
                Unable to load your client information. Please contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="mb-2 text-2xl font-bold text-black md:text-3xl">Client Dashboard</h1>
          <p className="text-black/70">
            Welcome back, {clientData.firstname} {clientData.lastname} from{' '}
            {clientData.organization}
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-xl bg-white p-4 shadow-lg md:p-6"
          >
            <div className="flex items-center gap-3">
              <AcademicCapIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-black">Sessions Completed</p>
                <p className="text-2xl font-bold text-black">{stats.sessionsCompleted}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="rounded-xl bg-white p-4 shadow-lg md:p-6"
          >
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-sm text-black">Milestones Achieved</p>
                <p className="text-2xl font-bold text-black">{stats.milestonesAchieved}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-xl bg-white p-4 shadow-lg md:p-6"
          >
            <div className="flex items-center gap-3">
              <BookOpenIcon className="h-8 w-8 text-indigo-600" />
              <div>
                <p className="text-sm text-black">Resources Downloaded</p>
                <p className="text-2xl font-bold text-black">{stats.resourcesDownloaded}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-xl bg-white p-4 shadow-lg md:p-6"
          >
            <div className="flex items-center gap-3">
              <StarIcon className="h-8 w-8 text-amber-600" />
              <div>
                <p className="text-sm text-black">Coach Rating</p>
                <p className="text-2xl font-bold text-black">{stats.coachRating}</p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          {tabs.map(tab => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'border border-black/20 bg-white text-black hover:bg-black/5 hover:text-black'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'dashboard' && (
              <TrainingDashboard onNavigateToTraining={onNavigateToTraining} />
            )}
            {activeTab === 'milestones' && (
              <MilestoneTracking onNavigateToTraining={onNavigateToTraining} />
            )}
            {activeTab === 'coach' && (
              <CoachView
                assigned={clientData.assigned}
                assignedEmail={clientData.assignedEmail}
                assignedCoachName={clientData.assignedCoachName}
                assignedCoachEmail={clientData.assignedCoachEmail}
              />
            )}
            {activeTab === 'tasks' && <TasksView />}
            {activeTab === 'session-notes' && <SessionNotesView />}
            {activeTab === 'resources' && <ResourceHub />}
            {activeTab === 'weeklyreports' && <WeeklyReportsUpload />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ClientsCRM;
